/**
 * Loads provider SDKs on demand and exchanges their tokens for an app session.
 *
 * Provider ids come from the backend at runtime rather than build-time env vars,
 * so a single frontend image stays valid across environments.
 */
import axios from './api-axios';

export interface SsoProviderConfig {
  google: { enabled: boolean; clientId: string };
  facebook: { enabled: boolean; appId: string };
  apple: { enabled: boolean; clientId: string };
}

export type SsoProvider = 'google' | 'facebook' | 'apple';

export const fetchSsoConfig = async (): Promise<SsoProviderConfig | null> => {
  try {
    const res = await axios.get('/api/auth/oauth/config');
    return res.data as SsoProviderConfig;
  } catch {
    // Sign-in with a password must keep working even if this call fails.
    return null;
  }
};

const loadScript = (src: string, id: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const el = document.createElement('script');
    el.src = src;
    el.id = id;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.appendChild(el);
  });

/** Trades a provider token for our own JWT and stores it exactly as password login does. */
const exchange = async (provider: SsoProvider, token: string): Promise<void> => {
  const res = await axios.post(`/api/auth/oauth/${provider}`, { token });
  localStorage.setItem('SavedToken', 'Bearer ' + res.data['jwt-token']);
};

export const signInWithGoogle = (clientId: string): Promise<void> =>
  new Promise(async (resolve, reject) => {
    try {
      await loadScript('https://accounts.google.com/gsi/client', 'google-gsi');
      const google = (window as any).google;
      if (!google?.accounts?.id) throw new Error('Google SDK unavailable');

      google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: { credential?: string }) => {
          try {
            if (!response.credential) throw new Error('No credential returned');
            await exchange('google', response.credential);
            resolve();
          } catch (e) {
            reject(e);
          }
        },
      });
      google.accounts.id.prompt();
    } catch (e) {
      reject(e);
    }
  });

export const signInWithFacebook = (appId: string): Promise<void> =>
  new Promise(async (resolve, reject) => {
    try {
      await loadScript('https://connect.facebook.net/en_US/sdk.js', 'facebook-sdk');
      const FB = (window as any).FB;
      if (!FB) throw new Error('Facebook SDK unavailable');

      FB.init({ appId, cookie: true, xfbml: false, version: 'v19.0' });
      FB.login((response: any) => {
        const token = response?.authResponse?.accessToken;
        if (!token) {
          reject(new Error('Facebook sign-in was cancelled'));
          return;
        }
        exchange('facebook', token).then(resolve).catch(reject);
      }, { scope: 'email' });
    } catch (e) {
      reject(e);
    }
  });

export const signInWithApple = (clientId: string): Promise<void> =>
  new Promise(async (resolve, reject) => {
    try {
      await loadScript(
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
        'apple-sdk'
      );
      const AppleID = (window as any).AppleID;
      if (!AppleID) throw new Error('Apple SDK unavailable');

      AppleID.auth.init({
        clientId,
        scope: 'email',
        redirectURI: window.location.origin,
        usePopup: true,
      });
      const response = await AppleID.auth.signIn();
      const token = response?.authorization?.id_token;
      if (!token) throw new Error('Apple sign-in returned no token');
      await exchange('apple', token);
      resolve();
    } catch (e) {
      reject(e);
    }
  });

export const signInWith = async (provider: SsoProvider, config: SsoProviderConfig): Promise<void> => {
  if (provider === 'google') return signInWithGoogle(config.google.clientId);
  if (provider === 'facebook') return signInWithFacebook(config.facebook.appId);
  return signInWithApple(config.apple.clientId);
};
